import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Printer, CheckCircle, Clock, Package,
  Truck, CheckCheck, XCircle, ArrowRight, Phone, Mail, MapPin, StickyNote, History,
} from 'lucide-react';
import OrderLabelModal from '../../components/admin/OrderLabelModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending:    { label: 'Da confermare',   bg: 'bg-yellow-100',  text: 'text-yellow-800',  dot: 'bg-yellow-400',  border: 'border-yellow-300'  },
  to_prepare: { label: 'Da preparare',    bg: 'bg-blue-100',    text: 'text-blue-800',    dot: 'bg-blue-500',    border: 'border-blue-300'    },
  preparing:  { label: 'In preparazione', bg: 'bg-purple-100',  text: 'text-purple-800',  dot: 'bg-purple-400',  border: 'border-purple-300'  },
  ready:      { label: 'Pronto',          bg: 'bg-teal-100',    text: 'text-teal-800',    dot: 'bg-teal-400',    border: 'border-teal-300'    },
  shipped:    { label: 'Spedito',         bg: 'bg-indigo-100',  text: 'text-indigo-800',  dot: 'bg-indigo-400',  border: 'border-indigo-300'  },
  completed:  { label: 'Completato',      bg: 'bg-green-100',   text: 'text-green-800',   dot: 'bg-green-400',   border: 'border-green-300'   },
  cancelled:  { label: 'Annullato',       bg: 'bg-red-100',     text: 'text-red-800',     dot: 'bg-red-400',     border: 'border-red-300'     },
};

const STATUS_ACTIONS = {
  pending:    { next: 'to_prepare', label: 'Conferma e prepara', icon: CheckCircle },
  to_prepare: { next: 'preparing',  label: 'Inizia preparazione', icon: Clock },
  preparing:  { next: 'ready',      label: 'Segna come pronto',  icon: Package },
  ready:      { next: 'shipped',    label: 'Spedito',            icon: Truck },
  shipped:    { next: 'completed',  label: 'Completa',           icon: CheckCheck },
};

const SHOW_LABEL_ON = ['to_prepare', 'shipped'];
const SHOW_WA_ON    = ['to_prepare'];

// Ordered status steps for timeline
const STATUS_STEPS = ['pending', 'to_prepare', 'preparing', 'ready', 'shipped', 'completed'];

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatPrice(price) {
  if (!price || price === 0) return '—';
  return `€ ${Number(price).toFixed(2).replace('.', ',')}`;
}

function parseItems(items) {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'string') {
    try {
      const parsed = JSON.parse(items);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'string') {
        const parsedAgain = JSON.parse(parsed);
        return Array.isArray(parsedAgain) ? parsedAgain : [];
      }
    } catch { return []; }
  }
  return [];
}

function parseHistory(history) {
  if (!history) return [];
  if (Array.isArray(history)) return history;
  if (typeof history === 'string') {
    try {
      const parsed = JSON.parse(history);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  return [];
}

function StatusTimeline({ currentStatus }) {
  const currentIdx = STATUS_STEPS.indexOf(currentStatus);
  const isCancelled = currentStatus === 'cancelled';

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
      <p className="font-body text-xs font-semibold uppercase tracking-wide text-gray-400 mb-4">Avanzamento ordine</p>

      {isCancelled ? (
        <div className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="font-body text-sm font-semibold text-red-700">Ordine annullato</span>
        </div>
      ) : (
        <div className="flex items-center gap-0 overflow-x-auto pb-1">
          {STATUS_STEPS.map((s, i) => {
            const sc = STATUS_CONFIG[s] || {};
            const isDone = i < currentIdx;
            const isCurrent = i === currentIdx;
            const isLast = i === STATUS_STEPS.length - 1;

            return (
              <div key={s} className="flex items-center flex-shrink-0">
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                    isDone    ? 'bg-green-500 border-green-500' :
                    isCurrent ? `${sc.bg} ${sc.border || 'border-gray-300'} border-2` :
                                'bg-gray-100 border-gray-200'
                  }`}>
                    {isDone ? (
                      <CheckCheck className="w-4 h-4 text-white" />
                    ) : (
                      <span className={`w-2 h-2 rounded-full ${isCurrent ? sc.dot : 'bg-gray-300'}`} />
                    )}
                  </div>
                  <span className={`text-center font-body whitespace-nowrap leading-tight ${
                    isCurrent ? 'text-xs font-semibold text-gray-800' : 'text-xs text-gray-400'
                  }`} style={{ maxWidth: '68px' }}>
                    {sc.label || s}
                  </span>
                </div>
                {!isLast && (
                  <div className={`h-0.5 w-8 mx-1 flex-shrink-0 mb-5 ${i < currentIdx ? 'bg-green-400' : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminOrdineDetail() {
  const { id } = useParams();
  const [order, setOrder]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [updating, setUpdating]   = useState(false);
  const [labelState, setLabelState] = useState(null);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${id}`);
      setOrder(res.data);
    } catch {
      toast.error('Ordine non trovato');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrder(); }, [id]); // eslint-disable-line

  const changeStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await axios.put(`${API}/orders/${id}`, { status: newStatus });
      const updated = res.data;
      setOrder(updated);
      toast.success('Stato aggiornato');
      if (SHOW_LABEL_ON.includes(newStatus)) {
        setLabelState({ order: updated, showWhatsApp: SHOW_WA_ON.includes(newStatus) });
      }
    } catch {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-2xl h-32 animate-pulse border border-gray-100" />)}
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-20">
        <p className="font-body text-gray-400 text-lg mb-4">Ordine non trovato</p>
        <Link to="/admin/ordini" className="font-body text-primary hover:underline text-sm">
          ← Torna agli ordini
        </Link>
      </div>
    );
  }

  const sc = STATUS_CONFIG[order.status] || { label: order.status, bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-400' };
  const action = STATUS_ACTIONS[order.status];
  const isTerminal = ['completed', 'cancelled'].includes(order.status);
  const orderNum = order.id.slice(-8).toUpperCase();
  const history = parseHistory(order.status_history);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Back button + header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          to="/admin/ordini"
          className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h2 className="font-heading text-xl font-bold text-gray-900">
            Ordine #{orderNum}
          </h2>
          <p className="font-body text-xs text-gray-400">{formatDate(order.created_at)}</p>
        </div>
        <div className="ml-auto">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-body text-xs font-semibold ${sc.bg} ${sc.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
            {sc.label}
          </span>
        </div>
      </div>

      {/* Timeline */}
      <StatusTimeline currentStatus={order.status} />

      {/* Actions */}
      {!isTerminal && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
          <p className="font-body text-xs font-semibold uppercase tracking-wide text-gray-400 mb-4">Azioni</p>
          <div className="space-y-2">
            {action && (
              <button
                onClick={() => changeStatus(action.next)}
                disabled={updating}
                className="w-full flex items-center gap-3 bg-primary-dark hover:bg-primary text-white py-3 px-4 rounded-xl font-body font-semibold text-sm transition-colors disabled:opacity-60"
              >
                <action.icon className="w-4 h-4" />
                {action.label}
                <ArrowRight className="w-4 h-4 ml-auto" />
              </button>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setLabelState({ order, showWhatsApp: false })}
                className="flex-1 flex items-center justify-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 py-2.5 rounded-xl font-body text-sm transition-colors"
              >
                <Printer className="w-4 h-4" />
                Stampa etichetta
              </button>
              <button
                onClick={() => changeStatus('cancelled')}
                disabled={updating}
                className="flex-1 flex items-center justify-center gap-2 border border-red-200 hover:bg-red-50 text-red-600 py-2.5 rounded-xl font-body text-sm transition-colors disabled:opacity-60"
              >
                <XCircle className="w-4 h-4" />
                Annulla ordine
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print label for terminal states */}
      {isTerminal && (
        <div className="mb-5">
          <button
            onClick={() => setLabelState({ order, showWhatsApp: false })}
            className="flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl font-body text-sm transition-colors"
          >
            <Printer className="w-4 h-4" />
            Stampa etichetta
          </button>
        </div>
      )}

      {/* Customer info */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
        <p className="font-body text-xs font-semibold uppercase tracking-wide text-gray-400 mb-4">Dati cliente</p>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="font-heading font-bold text-gray-600 text-sm">
                {order.customer_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-body font-semibold text-gray-900">{order.customer_name}</p>
            </div>
          </div>
          <a
            href={`tel:${order.customer_phone}`}
            className="flex items-center gap-3 text-primary hover:text-primary/80 transition-colors"
          >
            <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="font-body text-sm">{order.customer_phone}</span>
          </a>
          {order.customer_email && (
            <a
              href={`mailto:${order.customer_email}`}
              className="flex items-center gap-3 text-primary hover:text-primary/80 transition-colors"
            >
              <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="font-body text-sm">{order.customer_email}</span>
            </a>
          )}
          {order.customer_address && (
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <span className="font-body text-sm text-gray-700">{order.customer_address}</span>
            </div>
          )}
          {order.notes && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3 mt-2">
              <StickyNote className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-body text-xs font-semibold text-amber-700 mb-0.5">Note del cliente</p>
                <p className="font-body text-sm text-gray-700">{order.notes}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Audit trail */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-gray-400" />
          <p className="font-body text-xs font-semibold uppercase tracking-wide text-gray-400">Storico documentabile</p>
        </div>
        {history.length === 0 ? (
          <p className="font-body text-sm text-gray-400">Nessun evento registrato.</p>
        ) : (
          <div className="space-y-3">
            {history.slice().reverse().map((event, i) => (
              <div key={`${event.at || i}-${i}`} className="flex gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-body text-sm font-medium text-gray-900">{event.message || event.type}</p>
                  <p className="font-body text-xs text-gray-400">{formatDate(event.at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order items */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <p className="font-body text-xs font-semibold uppercase tracking-wide text-gray-400 mb-4">Articoli ordinati</p>
        <div className="space-y-2">
          {parseItems(order.items).map((item, i) => (
            <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
              {item.product_image && (
                <img
                  src={item.product_image}
                  alt={item.product_name}
                  className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-body font-medium text-gray-900">{item.product_name}</p>
                {(item.codart || item.ean || item.name_mdd) && (
                  <p className="font-body text-xs text-gray-500 mt-0.5">
                    {item.codart ? `CodArt: ${item.codart}` : ''}
                    {item.codart && item.ean ? ' · ' : ''}
                    {item.ean ? `EAN: ${item.ean}` : ''}
                    {item.name_mdd ? ` · MDD: ${item.name_mdd}` : ''}
                  </p>
                )}
                <p className="font-body text-xs text-gray-400">
                  {item.price > 0 ? `€ ${item.price.toFixed(2)} × ${item.quantity}` : `Qty: ${item.quantity}`}
                </p>
              </div>
              {item.price > 0 && (
                <span className="font-body font-semibold text-primary flex-shrink-0">
                  {formatPrice(item.price * item.quantity)}
                </span>
              )}
            </div>
          ))}
        </div>
        {order.total > 0 && (
          <div className="flex justify-between font-body font-semibold text-gray-900 mt-4 pt-4 border-t border-gray-200">
            <span>Totale ordine</span>
            <span className="text-primary text-lg">{formatPrice(order.total)}</span>
          </div>
        )}
      </div>

      {/* Print label modal */}
      {labelState && (
        <OrderLabelModal
          order={labelState.order}
          showWhatsApp={labelState.showWhatsApp}
          onClose={() => setLabelState(null)}
        />
      )}
    </div>
  );
}
